import { useNavigate } from 'react-router-dom';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  TrashIcon,
  PlayIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Checkbox, Badge, Button } from '@/components/ui';
import { formatDate } from '@/utils/formatters';
import { cn } from '@/utils/cn';
import type { AnnotationTemplate } from '@/services/templateApi';

export interface TemplateListViewProps {
  templates: AnnotationTemplate[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  /** Row click — navigate to template details */
  onSelect: (template: AnnotationTemplate) => void;
  onDelete: (id: string) => void;
  onEvaluate: (id: string) => void;
  sortField: 'name' | 'created_at';
  sortDir: 'asc' | 'desc';
  onSort: (field: 'name' | 'created_at') => void;
}

/**
 * Table-view for the template library.
 *
 * Columns: Checkbox | Name | Channel | Visibility | Phases | Created | Actions
 *
 * At < md breakpoint the component renders nothing — the parent should fall
 * back to the gallery view at that point.
 */
// Exported as both named and default to support both import styles.
export function TemplateListView({
  templates,
  selectedIds,
  onToggleSelect,
  onSelect,
  onDelete,
  onEvaluate,
  sortField,
  sortDir,
  onSort,
}: TemplateListViewProps) {
  const navigate = useNavigate();

  if (templates.length === 0) {
    return null;
  }

  function SortIndicator({ field }: { field: 'name' | 'created_at' }) {
    if (sortField !== field) {
      return <ChevronUpDownIcon className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />;
    }
    if (sortDir === 'asc') {
      return <ChevronUpIcon className="h-3.5 w-3.5 text-accent" aria-hidden="true" />;
    }
    return <ChevronDownIcon className="h-3.5 w-3.5 text-accent" aria-hidden="true" />;
  }

  function SortableHeader({
    field,
    children,
  }: {
    field: 'name' | 'created_at';
    children: React.ReactNode;
  }) {
    return (
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-text-muted hover:text-text-secondary transition-colors"
        aria-label={`Sort by ${field === 'name' ? 'name' : 'date created'}`}
      >
        {children}
        <SortIndicator field={field} />
      </button>
    );
  }

  return (
    /* Hidden on mobile — parent swaps back to gallery below md */
    <div className="hidden md:block overflow-x-auto rounded-xl border border-border-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-dark-elevated-1 border-b border-border-subtle">
            {/* Checkbox column */}
            <th className="w-10 px-4 py-3 text-left">
              <span className="sr-only">Select</span>
            </th>

            {/* Name */}
            <th className="px-4 py-3 text-left">
              <SortableHeader field="name">Name</SortableHeader>
            </th>

            {/* Channel */}
            <th className="px-4 py-3 text-left">
              <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Channel
              </span>
            </th>

            {/* Visibility */}
            <th className="px-4 py-3 text-left">
              <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Visibility
              </span>
            </th>

            {/* Phases */}
            <th className="px-4 py-3 text-left">
              <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Phases
              </span>
            </th>

            {/* Created */}
            <th className="px-4 py-3 text-left">
              <SortableHeader field="created_at">Created</SortableHeader>
            </th>

            {/* Actions */}
            <th className="px-4 py-3 text-right">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => {
            const isSelected = selectedIds.has(template.id);
            const phaseCount = template.phases?.length ?? null;

            return (
              <tr
                key={template.id}
                onClick={() => onSelect(template)}
                className={cn(
                  'border-b border-border-subtle last:border-0 cursor-pointer transition-colors',
                  'hover:bg-dark-elevated-2',
                  isSelected && 'bg-accent/5',
                )}
              >
                {/* Checkbox */}
                <td
                  className="w-10 px-4 py-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect(template.id)}
                    aria-label={`Select ${template.name}`}
                  />
                </td>

                {/* Name */}
                <td className="px-4 py-3">
                  <span className="font-medium text-text-primary">{template.name}</span>
                  {template.description && (
                    <p className="text-xs text-text-muted mt-0.5 line-clamp-1">
                      {template.description}
                    </p>
                  )}
                </td>

                {/* Channel */}
                <td className="px-4 py-3">
                  {template.channel_slug ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/channels/${template.channel_slug}`);
                      }}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-info/15 text-info hover:bg-info/25 transition-colors"
                      aria-label={`View channel ${template.channel_slug}`}
                    >
                      {template.channel_slug}
                    </button>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>

                {/* Visibility */}
                <td className="px-4 py-3">
                  {template.visibility === 'public' ? (
                    <Badge variant="warning" size="sm">
                      <GlobeAltIcon className="h-3 w-3 mr-1 inline" aria-hidden="true" />
                      Public
                    </Badge>
                  ) : (
                    <Badge variant="secondary" size="sm">
                      <LockClosedIcon className="h-3 w-3 mr-1 inline" aria-hidden="true" />
                      Private
                    </Badge>
                  )}
                </td>

                {/* Phases */}
                <td className="px-4 py-3 text-text-secondary">
                  {phaseCount !== null ? phaseCount : '—'}
                </td>

                {/* Created */}
                <td className="px-4 py-3 text-text-muted text-xs">
                  {formatDate(template.created_at)}
                </td>

                {/* Actions */}
                <td
                  className="px-4 py-3 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEvaluate(template.id)}
                      aria-label={`Evaluate ${template.name}`}
                    >
                      <PlayIcon className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                      Evaluate
                    </Button>
                    <button
                      type="button"
                      onClick={() => onDelete(template.id)}
                      className="text-text-muted hover:text-danger transition-colors p-1 rounded-md hover:bg-danger/10"
                      aria-label={`Delete ${template.name}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Default export for barrel re-exports.
export default TemplateListView;
